import React from "react";
import { Button } from "../ui/button";
import { ChevronLeft, Printer, FileDown, AlertTriangle } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { fmtKind, unitLabel } from "../../lib/units";
import { PsychrometricChart } from "../PsychrometricChart";
import { isModelN, isACB, pickCascadeOperatingPoint, extractRoomDelivered, formatSizingFootnote, getRoomStaticPressureInwg, MODEL_D_CAPACITY_DERATE } from "../../lib/wizardCalc";
import { paSensibleCapacity, paLatentCapacity, dewpointF } from "../../lib/psychro";
import { generateRoomReportPDF } from "../../lib/pdfReport";
import { unitLengthLabel } from "../../lib/units";
import ModelNDetailView from "./ModelNDetailView";
import ModelODetailView, { EngineSelectionSummary } from "./ModelODetailView";

function isModelO(model) {
  return model === "Model O-1" || model === "Model O-2";
}

// Accepts (room, raw, isSI) — legacy three-arg signature.
// New optional fourth arg `summary` lets the display name still resolve a
// length when `raw` was stripped on save (p3c-strip-raw). Without this the
// header reads "Model C1" instead of "Model C1-1200" on any room report
// regenerated from a freshly-loaded project.
function modelDisplayName(room, raw, isSI = false, summary = null) {
  if (!room?.unit_model) return "—";
  if (isModelN(room.unit_model)) return room.unit_model;
  const len =
    raw?.system_parameters?.length_ft ||
    raw?.results?.[0]?.length_ft ||
    summary?.selected_length_ft ||
    summary?.plan?.length_ft ||
    null;
  if (!len) return room.unit_model;
  return `${room.unit_model}-${unitLengthLabel(len, isSI)}`;
}

// Pick the speed row matching the user-chosen ACB fan speed (advanced.acb_fan_speed),
// or the engine's optimal_point.operating_speed, falling back to MED (AHRI 1335 rating
// speed for active chilled beams), then HIGH, then the last available speed.
// Returns the matched speed entry from the speeds array.
function pickSpeedRow(speeds, project) {
  if (!speeds || speeds.length === 0) return {};
  // advanced.acb_fan_speed is stored as uppercase ("LOW"/"MED"/"HIGH");
  // speed entries are lowercase ("low"/"med"/"high").
  const acbKey = (project?.advanced?.acb_fan_speed || "").toLowerCase();
  if (acbKey) {
    const found = speeds.find((s) => s.speed === acbKey);
    if (found) return found;
  }
  return speeds.find((s) => s.speed === "med") ||
         speeds.find((s) => s.speed === "high") ||
         speeds[speeds.length - 1] || {};
}

// Banner heading for the green capacity block.
// AHRI 1335 single-basis lock: always returns the AHRI 1335 / Q_r label.
// The water_side branch is kept as a defensive fallthrough only — it should
// never trigger now that the UI selector is removed.
export function bannerLabelForBasis(basis) {
  // AHRI 1335 (Q_r) is the only display basis — basis arg is always "room_load".
  if (basis === "water_side") return "Coil Water-Side Capacity (Qᴄ)"; // defensive fallthrough
  return "ROOM-DELIVERED CAPACITY (Q_r, AHRI 1335)";
}

// Compute ROOM-AGGREGATE delivered capacity (sum across all sub-units × their qty).
// For a single-unit room, this equals one unit's performance.
function computeDeliveredCapacity(result, isN, project, room) {
  if (!result?.ok) return null;
  const plan = result.summary?.plan;
  const units =
    plan?.units && plan.units.length
      ? plan.units
      : [{ qty: plan?.qty ?? 1, result, model: null }];

  // PA-aware capacity calc — match the per-room PA breakdown so the green
  // banner stays consistent with the Cooling/Heating tables below.
  const dc = project?.designConditions || {};
  const T_room = parseFloat(dc.room_db_f) || 75;
  const RH_room = parseFloat(dc.room_rh) || 50;
  const T_pa = parseFloat(dc.pa_eat_db_f) || parseFloat(dc.outdoor_db_f) || 95;
  const RH_pa = parseFloat(dc.pa_eat_rh) || 50;
  const T_water_in = parseFloat(dc.water_entering_f) || 45;
  const T_room_w = parseFloat(dc.room_db_winter_f) || 70;
  // dewpoint
  const T_C = (T_room - 32) * 5 / 9;
  const a = 17.27, b = 237.3;
  const alpha = (a * T_C) / (b + T_C) + Math.log(Math.max(RH_room / 100, 0.0001));
  const dp_C = (b * alpha) / (a - alpha);
  const roomDewpoint = dp_C * 9 / 5 + 32;
  const coilLatentSuppressed = T_water_in > (roomDewpoint - 2);

  // Per-unit display rule: for a non-split room (N identical units, qty>1) the banner
  // shows one unit's delivered capacity so engineers can read a single-unit spec directly.
  // For a true split room (multiple sub-units with distinct lengths via UnitSplitModal) we
  // sum sub-units weighted by their own qty so the total reflects the full room.
  // SummaryTab.jsx::roomAggregate uses the identical isSplit guard — keep them in sync.
  const distinctLengths = new Set(units.map((u) => u.length_ft).filter((l) => l != null));
  const isSplit = units.length > 1 && distinctLengths.size > 1;

  // AHRI 1335 single-basis lock: basis is always room_load.
  const totals = { total: 0, sensible: 0, latent: 0, heating: 0, airflow: 0, basis: "room_load" };
  units.forEach((u) => {
    const sQty = isSplit ? (u.qty || 1) : 1;
    const r = u.result || result;
    if (!r?.ok) return;
    const subIsN = u.model ? isModelN(u.model) : isN;
    const raw = r.raw;
    // Summary-fallback path: when raw is missing (stripped on save per
    // p3c-strip-raw), read directly from the persisted summary fields.
    // This is what lets a freshly-loaded project still render correct
    // green-banner totals in the Detailed Rooms PDF without forcing the
    // engineer to re-run Perform Selection just to populate raw.
    if (!raw && r.summary) {
      const s = r.summary;
      const total = parseFloat(s.cooling_total_btuh);
      if (Number.isFinite(total) && total > 0) {
        const heatNet = parseFloat(s.heating_btuh);
        const airflow = parseFloat(s.air_total_cfm) || parseFloat(s.oa_cfm) || parseFloat(r.summary.plan?.per_unit_oa) || 0;
        totals.total    += total                                       * sQty;
        totals.sensible += (parseFloat(s.cooling_sensible_btuh) || 0)  * sQty;
        totals.latent   += (parseFloat(s.cooling_latent_btuh) || 0)    * sQty;
        totals.heating  += (Number.isFinite(heatNet) ? heatNet : 0)    * sQty;
        totals.airflow  += airflow                                     * sQty;
        return;
      }
    }
    let pu;
    if (subIsN) {
      const ai = raw?.summary_table?.air_side || {};
      pu = {
        total: ai.total_capacity_btuh ?? 0,
        sensible: ai.sensible_capacity_btuh ?? 0,
        latent: ai.latent_capacity_btuh ?? 0,
        heating: 0,
        airflow: ai.total_airflow_cfm ?? 0,
      };
      // Model N heating: backend returns a separate top-level heating block (not heating_optimal).
      // When present, add its achieved capacity to the banner's heating total.
      if (raw?.heating?.achieved_capacity_btuh) {
        pu.heating = parseFloat(raw.heating.achieved_capacity_btuh) || 0;
      }
    } else {
      const best = raw?.results?.[0] || {};
      const speeds = best?.cooling?.speeds || [];
      const isPA = best?.cooling?.pa_mode === true ||
        (speeds.length === 1 && speeds[0]?.speed === "pa");
      if (isPA) {
        // PA models: delegate to extractRoomDelivered which reads opt.air_*_btuh
        // (post-mixing, post-water-modulation) — never speed[0] row.
        // This matches the Summary tab and fixes the overcount bug.
        const d = extractRoomDelivered(best, project, room);
        pu = {
          total:    d.total    ?? 0,
          sensible: d.sensible ?? 0,
          latent:   d.latent   ?? 0,
          heating:  (d.heating ?? 0) + (d.pa_heat_contribution ?? 0),
          airflow:  d.airflow  ?? 0,
          basis: "room_load",
        };
      } else {
        // Delegate to the shared extractRoomDelivered helper so the Detail
        // banner always reads the same fields as Summary tab (B.7).
        // No unmodulated speed-row fallbacks — if opt is missing, total is null.
        // Net heating = coil air-side + pa_heat_contribution — mirrors the
        // Summary tab's Net Heating column. See [[project-summary-vs-detail-calcs]].
        const d = extractRoomDelivered(best, project, room);
        // AHRI 1335 (Q_r) is the only reporting basis. Banner always shows
        // room-delivered (Qr) totals. water_side branch removed.
        // For non-PA models pa_heat_contribution is 0.
        pu = {
          total:    d.total   ?? 0,
          sensible: d.sensible ?? 0,
          latent:   d.latent  ?? 0,
          heating:  (d.heating ?? 0) + (d.pa_heat_contribution ?? 0),
          airflow:  d.airflow,
          basis:    "room_load",
        };
      }
    }
    totals.total += pu.total * sQty;
    totals.sensible += pu.sensible * sQty;
    totals.latent += pu.latent * sQty;
    totals.heating += pu.heating * sQty;
    totals.airflow += pu.airflow * sQty;
    if (pu.basis) totals.basis = pu.basis;
  });
  return totals;
}

function Section({ title, children }) {
  return (
    <div data-pdf-section className="border border-border rounded overflow-hidden mb-2 break-inside-avoid">
      <div className="bg-muted px-3 py-1.5 text-sm font-semibold border-b border-border text-foreground">
        {title}
      </div>
      <div className="p-2 text-xs">{children}</div>
    </div>
  );
}

/**
 * ModelNDetail — thin wrapper that dispatches to the comprehensive
 * ModelNDetailView component. Kept as a named local function so the
 * existing call-sites in this file (single-unit and sub-unit paths)
 * need no changes.
 */
function ModelNDetail({ raw, isSI, showChart = true, unitModel }) {
  const isNf = unitModel === "Model N-f";
  return (
    <ModelNDetailView raw={raw} isSI={isSI} showChart={showChart} isNf={isNf} />
  );
}

/**
 * ModelODetail — thin wrapper dispatching to ModelODetailView.
 */
function ModelODetail({ raw, isSI, showChart = true, unitModel, project = null, room = null }) {
  const isO2 = unitModel === "Model O-2";
  return (
    <ModelODetailView raw={raw} isSI={isSI} showChart={showChart} isO2={isO2} project={project} room={room} />
  );
}

function MultiLengthDetail({ raw, summary, isSI, project, room, showChart = false, showHeating = true }) {
  const results = raw?.results || [];

  // ── Summary-fallback path ────────────────────────────────────────────────
  // When raw is absent (stripped on save per p3c-strip-raw policy) but the
  // persisted summary has all the per-coil fields the engineer needs, render
  // a minimal but complete detail block so the Detailed Rooms PDF is useful
  // without forcing a re-run of Perform Selection.
  //
  // Only applies to ACB / FACB PA models (is_pa_model = true in summary).
  // Model N falls through to the "No detailed result" placeholder as before —
  // its summary does not carry the same per-coil breakdown.
  if (!results.length) {
    const s = summary;
    if (s?.is_pa_model && (s.pa_sensible_btuh != null || s.coil_sensible_btuh != null)) {
      const isACBRoom = isACB(room?.unit_model);
      const dc = project?.designConditions || {};
      const T_room = parseFloat(dc.room_db_f) || 75;
      const RH_room = parseFloat(dc.room_rh) || 50;
      const T_water_in_cool = parseFloat(dc.water_entering_f) || 45;
      const roomDewpoint = dewpointF(T_room, RH_room);
      const coilLatentSuppressed = T_water_in_cool > (roomDewpoint - 2);

      const paSensCool   = parseFloat(s.pa_sensible_btuh)  || 0;
      const paLatCool    = parseFloat(s.pa_latent_btuh)     || 0;
      const coilSensCool = parseFloat(s.coil_sensible_btuh) || 0;
      const coilLatCool  = coilLatentSuppressed ? 0 : (parseFloat(s.coil_latent_btuh) || 0);
      const totalSensCool = paSensCool + coilSensCool;
      const totalLatCool  = paLatCool  + coilLatCool;

      const cooling_gpm  = parseFloat(s.water_flow_gpm)          || 0;
      const cooling_pd   = parseFloat(s.water_pd_fth2o)          || null;
      const nozzle       = s.nozzle_label || "—";
      const ncVal        = s.nc_level;
      const paCfm        = parseFloat(s.oa_cfm) || parseFloat(s.air_total_cfm) || 0;
      const noCoilPipes  = project?.advanced?.no_coil_pipes ?? 2;
      const noCoilCircuits = s.selected_circuits;

      // Cooling SP: prefer room override → project default → null
      const resolvedSpInwg = getRoomStaticPressureInwg(room, null, project);

      // Heating from summary
      const heat_total  = parseFloat(s.heating_btuh)             || 0;
      const paHeatContrib = parseFloat(s.heating_pa_btuh)        || 0;
      const coilHeat    = parseFloat(s.coil_heating_btuh)        || 0;
      const heat_gpm    = parseFloat(s.heating_water_flow_gpm)   || 0;
      const heat_pd     = parseFloat(s.heating_water_pd_fth2o)   || null;

      // Synthetic optimalPoint for EngineSelectionSummary — populate the
      // fields it renders so the block shows rather than returns null.
      const syntheticOpt = {
        target_btuh_basis: s.capacity_basis || "room_load",
        water_flow_gpm: cooling_gpm || undefined,
        actual_pa_cfm: paCfm || undefined,
        nozzle_label: nozzle !== "—" ? nozzle : undefined,
      };

      return (
        <>
          {/* Engine Selection Summary — from saved summary fields */}
          <EngineSelectionSummary
            optimalPoint={syntheticOpt}
            isFACBFamily={false}
            isSI={isSI}
            noCoilPipes={noCoilPipes}
            selectedCircuits={noCoilCircuits ?? null}
          />
          {/* Water-side summary */}
          <Section title="Water Side">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
              <div>
                <div className="text-muted-foreground">Chilled Water Flow</div>
                <div className="font-semibold">{cooling_gpm > 0 ? `${fmtKind(cooling_gpm, "waterFlow", isSI)} ${unitLabel("waterFlow", isSI)}` : "—"}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Chilled Water PD</div>
                <div className="font-semibold">{cooling_pd != null ? `${fmtKind(cooling_pd, "pressure", isSI)} ${unitLabel("pressure", isSI)}` : "—"}</div>
              </div>
              {showHeating && (
                <>
                  <div>
                    <div className="text-muted-foreground">Heating Water Flow</div>
                    <div className="font-semibold">{heat_gpm > 0 ? `${fmtKind(heat_gpm, "waterFlow", isSI)} ${unitLabel("waterFlow", isSI)}` : "—"}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Heating Water PD</div>
                    <div className="font-semibold">{heat_pd != null ? `${fmtKind(heat_pd, "pressure", isSI)} ${unitLabel("pressure", isSI)}` : "—"}</div>
                  </div>
                </>
              )}
            </div>
          </Section>
          {/* Cooling — Primary-Air (Induction) Selection from summary */}
          <Section title="Cooling — Primary-Air (Induction) Selection">
            <div className="mb-2 grid grid-cols-2 md:grid-cols-6 gap-2 text-xs">
              <div>
                <div className="text-muted-foreground">PA Flow</div>
                <div className="font-semibold">{paCfm > 0 ? `${fmtKind(paCfm, "flow", isSI)} ${unitLabel("flow", isSI)}` : "—"}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Nozzle</div>
                <div className="font-semibold text-emerald-700 dark:text-emerald-400">{nozzle}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Static Pressure</div>
                <div className="font-semibold">
                  {resolvedSpInwg != null
                    ? `${fmtKind(resolvedSpInwg, "staticPressure", isSI)} ${unitLabel("staticPressure", isSI)}`
                    : "—"}
                </div>
              </div>
              <div>
                <div className="text-muted-foreground">Coil Mode</div>
                <div className="font-semibold">
                  {coilLatentSuppressed
                    ? <span className="text-amber-700 dark:text-amber-400">Sensible only</span>
                    : <span className="text-blue-700 dark:text-blue-400">Sensible + Latent</span>}
                </div>
              </div>
              <div>
                <div className="text-muted-foreground">NC Level</div>
                <div className="font-semibold" data-testid="room-airside-nc">
                  {ncVal != null ? Math.round(ncVal) : "—"}
                </div>
              </div>
            </div>
            <Table className="text-xs [&_th]:h-7 [&_th]:pt-0.5 [&_th]:pb-1.5 [&_td]:pt-0.5 [&_td]:pb-1.5 [&_td]:align-middle border-separate border-spacing-0">
              <TableHeader>
                <TableRow>
                  <TableHead></TableHead>
                  <TableHead className="text-right">PA Sens<br/>({unitLabel("capacity", isSI)})</TableHead>
                  <TableHead className="text-right">PA Lat<br/>({unitLabel("capacity", isSI)})</TableHead>
                  <TableHead className="text-right">Coil Sens<br/>({unitLabel("capacity", isSI)})</TableHead>
                  {!(isACBRoom || coilLatentSuppressed) && <TableHead className="text-right">Coil Lat<br/>({unitLabel("capacity", isSI)})</TableHead>}
                  <TableHead className="text-right font-semibold">Total Sens<br/>({unitLabel("capacity", isSI)})</TableHead>
                  {!isACBRoom && <TableHead className="text-right font-semibold">Total Lat<br/>({unitLabel("capacity", isSI)})</TableHead>}
                  {!isACBRoom && <TableHead className="text-right font-bold bg-emerald-100 dark:bg-emerald-950/40">Total Capacity<br/>({unitLabel("capacity", isSI)})</TableHead>}
                  <TableHead className="text-right">{unitLabel("waterFlow", isSI)}</TableHead>
                  <TableHead className="text-right">PD<br/>({unitLabel("pressure", isSI)})</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow className="bg-emerald-50 dark:bg-emerald-950/30 border-t-[6px] border-white h-8 [&>td]:py-0 [&>td]:align-middle">
                  <TableCell className="font-semibold">Cooling</TableCell>
                  <TableCell className="text-right">{fmtKind(paSensCool, "capacity", isSI)}</TableCell>
                  <TableCell className="text-right">{fmtKind(paLatCool, "capacity", isSI)}</TableCell>
                  <TableCell className="text-right">{fmtKind(coilSensCool, "capacity", isSI)}</TableCell>
                  {!(isACBRoom || coilLatentSuppressed) && <TableCell className="text-right">{fmtKind(coilLatCool, "capacity", isSI)}</TableCell>}
                  <TableCell className="text-right font-semibold">{fmtKind(totalSensCool, "capacity", isSI)}</TableCell>
                  {!isACBRoom && <TableCell className="text-right font-semibold">{fmtKind(totalLatCool, "capacity", isSI)}</TableCell>}
                  {!isACBRoom && <TableCell className="text-right font-bold bg-emerald-100 dark:bg-emerald-950/40">{fmtKind(totalSensCool + totalLatCool, "capacity", isSI)}</TableCell>}
                  <TableCell className="text-right">{cooling_gpm > 0 ? fmtKind(cooling_gpm, "waterFlow", isSI) : "—"}</TableCell>
                  <TableCell className="text-right">{cooling_pd != null ? fmtKind(cooling_pd, "pressure", isSI) : "—"}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Section>
          {showHeating && heat_total > 0 && (
            <Section title="Heating — Primary-Air (Induction) Selection">
              <Table className="text-xs [&_th]:h-7 [&_th]:pt-0.5 [&_th]:pb-1.5 [&_td]:pt-0.5 [&_td]:pb-1.5 [&_td]:align-middle border-separate border-spacing-0">
                <TableHeader>
                  <TableRow>
                    <TableHead></TableHead>
                    <TableHead className="text-right">PA Contribution<br/>({unitLabel("capacity", isSI)})</TableHead>
                    <TableHead className="text-right">Coil Heat<br/>({unitLabel("capacity", isSI)})</TableHead>
                    <TableHead className="text-right font-semibold">Total<br/>({unitLabel("capacity", isSI)})</TableHead>
                    <TableHead className="text-right">{unitLabel("waterFlow", isSI)}</TableHead>
                    <TableHead className="text-right">PD<br/>({unitLabel("pressure", isSI)})</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow className="bg-orange-50 dark:bg-orange-950/30 border-t-[6px] border-white h-8 [&>td]:py-0 [&>td]:align-middle">
                    <TableCell className="font-semibold">Heating</TableCell>
                    <TableCell className={`text-right ${paHeatContrib < 0 ? "text-rose-600" : ""}`}>{fmtKind(paHeatContrib, "capacity", isSI)}</TableCell>
                    <TableCell className="text-right">{fmtKind(coilHeat, "capacity", isSI)}</TableCell>
                    <TableCell className="text-right font-semibold">{fmtKind(heat_total, "capacity", isSI)}</TableCell>
                    <TableCell className="text-right">{heat_gpm > 0 ? fmtKind(heat_gpm, "waterFlow", isSI) : "—"}</TableCell>
                    <TableCell className="text-right">{heat_pd != null ? fmtKind(heat_pd, "pressure", isSI) : "—"}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Section>
          )}
          <p className="text-[10px] text-muted-foreground mt-1 italic">
            Psychrometric chart available after re-running Perform Selection.
          </p>
        </>
      );
    }
    return <div className="text-sm text-muted-foreground">No detailed result available.</div>;
  }
  const best = results[0];
  const speeds = best?.cooling?.speeds || [];
  const optimalPoint = best?.optimal_point || null;
  // Water-side numbers live at the LENGTH level on the multi-length endpoint
  // (water_flow_gpm is constant across speeds for a given length). For
  // Primary-Air (induction) models, the backend modulates water flow per
  // length so the speed row's `total_water_flow_gpm` is more accurate than
  // the un-modulated top-level value.
  const paSpeedForFlow = (speeds.length === 1 && speeds[0]?.speed === "pa") ? speeds[0] : null;
  const gpm = paSpeedForFlow?.total_water_flow_gpm ?? best.water_flow_gpm;
  const coilPd = best.coil_pd_fth2o;
  const accPd = best.accessory_pd;
  const totalPd = best.total_pd_fth2o ?? coilPd;
  const heatGpm = best.heating_optimal?.water_flow_gpm ?? best.heating?.water_flow_gpm ?? gpm;
  const heatPd = best.heating?.total_pd_fth2o ?? totalPd;

  return (
    <>
      {/* Engine Selection Summary — shown when optimal_point is present */}
      <EngineSelectionSummary
        optimalPoint={optimalPoint && Object.keys(optimalPoint).length > 0 ? optimalPoint : null}
        isFACBFamily={false}
        isSI={isSI}
        noCoilPipes={project?.advanced?.no_coil_pipes ?? 2}
        selectedCircuits={best?.circuits ?? null}
      />
      {/* Water-side summary (single value per length) */}
      <Section title="Water Side">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
          <div>
            <div className="text-muted-foreground">Chilled Water Flow</div>
            <div className="font-semibold">{fmtKind(gpm, "waterFlow", isSI)} {unitLabel("waterFlow", isSI)}</div>
          </div>
          <div>
            <div className="text-muted-foreground">Chilled Water PD (coil + acc)</div>
            <div className="font-semibold">
              {fmtKind(totalPd, "pressure", isSI)} {unitLabel("pressure", isSI)}
              {accPd != null && <span className="text-muted-foreground"> (coil {fmtKind(coilPd, "pressure", isSI)} + acc {fmtKind(accPd, "pressure", isSI)})</span>}
            </div>
          </div>
          {showHeating && (
            <>
              <div>
                <div className="text-muted-foreground">Heating Water Flow</div>
                <div className="font-semibold">{fmtKind(heatGpm, "waterFlow", isSI)} {unitLabel("waterFlow", isSI)}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Heating Water PD</div>
                <div className="font-semibold">{fmtKind(heatPd, "pressure", isSI)} {unitLabel("pressure", isSI)}</div>
              </div>
            </>
          )}
        </div>
      </Section>

      {(() => {
        // For Primary-Air (induction) models the backend returns a single
        // "pa" speed (no Lo/Med/Hi) and reports nozzle + PA-load details.
        // Render a dedicated PA breakdown table.
        const isPA = best?.cooling?.pa_mode === true ||
          (speeds.length === 1 && speeds[0]?.speed === "pa");
        if (isPA) {
          // ACB family (induction, no fan): coil is always sensible-only by
          // engineering convention — hide Coil Lat column regardless of EWT.
          const isACBRoom = isACB(room?.unit_model);

          const s = speeds[0] || {};
          const opt = best?.optimal_point || {};
          const nozzle = s.nozzle_label || s.nozzle || "—";
          // For PA / induction units the detail page must reflect ACTUAL
          // nozzle-resolved PA (what the unit physically delivers at the
          // chosen SP) — NOT the design PA. Prefer opt.actual_pa_cfm /
          // s.actual_pa_cfm so the table agrees with the green banner and
          // the saved summary. Bug 2026-06-27 (Vladimir): the previous
          // path used s.primary_cfm = design 99 CFM and psychro-recomputed
          // PA Sens at 633 W, while the unit only delivers 70 CFM PA so
          // the engine's stamped PA Sens is 447 W. The banner is correct;
          // the detail table was wrong.
          const paCfm = opt.actual_pa_cfm ?? s.actual_pa_cfm ?? s.primary_cfm ?? best.cooling?.pa_cfm ?? 0;
          const inductionRatio = s.induction_ratio;
          const staticP = s.induction_pressure_inwg ?? s.pa_pressure_inwg ?? best.cooling?.pa_pressure_inwg;

          // ---- Cooling computations ----
          const dc = project?.designConditions || {};
          const T_room = parseFloat(dc.room_db_f) || 75;
          const RH_room = parseFloat(dc.room_rh) || 50;
          const T_pa_cool = parseFloat(dc.pa_eat_db_f) || parseFloat(dc.outdoor_db_f) || 95;
          const RH_pa_cool = parseFloat(dc.pa_eat_rh) || 50;
          const T_water_in_cool = parseFloat(dc.water_entering_f) || 45;
          const roomDewpoint = dewpointF(T_room, RH_room);
          // User rule: coil latent = 0 if EWT > (room DP − 2). PA latent is
          // independent of EWT and always reflects PA-vs-room moisture.
          const coilLatentSuppressed = T_water_in_cool > (roomDewpoint - 2);

          // Model D derate: pa_total_btuh_input not in backend derate whitelist — Vladimir 2026-06-23.
          const isModelDRender = room?.unit_model === "Model D";
          const paDerateRender = isModelDRender ? MODEL_D_CAPACITY_DERATE : 1.0;
          // Prefer the engine's stamped values on optimal_point (computed at
          // the nozzle-resolved actual PA, post-water-modulation) so the
          // detail table agrees with the green banner and summary. Fall back
          // to a psychro recompute / speed-row read only for cached/legacy
          // responses that pre-date opt.pa_sensible_btuh stamping.
          // Bug fix 2026-06-27 (Vladimir).
          const _paSensFallback = paSensibleCapacity(paCfm, T_room, T_pa_cool) * paDerateRender;
          const _paLatFallback = paLatentCapacity(paCfm, T_room, RH_room, T_pa_cool, RH_pa_cool) * paDerateRender;
          const paSensCool = (parseFloat(opt.pa_sensible_btuh) || parseFloat(s.pa_sensible_btuh) || _paSensFallback);
          const paLatCool = (parseFloat(opt.pa_latent_btuh) || parseFloat(s.pa_latent_btuh) || _paLatFallback);
          const coilSensCool = parseFloat(opt.coil_sensible_btuh) || parseFloat(s.coil_sensible_btuh) || parseFloat(s.final_sensible_btuh) || 0;
          const coilLatCool = coilLatentSuppressed
            ? 0
            : (parseFloat(opt.coil_latent_btuh) || parseFloat(s.coil_latent_btuh) || parseFloat(s.final_latent_btuh) || 0);
          const totalSensCool = paSensCool + coilSensCool;
          const totalLatCool = paLatCool + coilLatCool;

          // LWT comes directly from the coil tables via the backend
          // (modulated values are mirrored onto the speed row).
          const cooling_gpm = parseFloat(s.total_water_flow_gpm) || parseFloat(best.water_flow_gpm) || 0;
          const lwtCool = parseFloat(s.leaving_water_f) || T_water_in_cool;

          // ---- Heating computations (PA contribution may be negative when PA < room) ----
          const T_room_w = parseFloat(dc.room_db_winter_f) || 70;
          const T_water_in_heat = parseFloat(dc.water_entering_heat_f) || parseFloat(dc.water_entering_winter_f) || 120;
          const heating = best.heating || {};
          const heatRow = heating.speeds?.[0] || {};
          // PA-mode heating: backend now mirrors modulated heating_optimal
          // onto speeds[0]. air_total_btuh = capacity delivered to the room.
          // Fallback to heating_optimal directly for older payloads.
          const heatOpt = best.heating_optimal || {};
          const heat_total = parseFloat(heatRow.air_total_btuh)
            || parseFloat(heatOpt.air_total_btuh)
            || parseFloat(heatRow.final_total_btuh)
            || 0;
          // PA contribution for HEATING: prefer the backend's
          // heating_optimal.pa_total_btuh_input (computed with WINTER PA temp
          // post C-2 BUG-2 fix). Fall back to client-side only for payloads
          // that pre-date the field; fallback uses pa_eat_db_winter_f, never
          // the summer pa_eat_db_f.
          const backendPaHeat = parseFloat(heatOpt.pa_total_btuh_input);
          const T_pa_winter = parseFloat(dc.pa_eat_db_winter_f) || parseFloat(dc.pa_eat_db_f) || 95;
          // pa_total_btuh_input not in backend _MODEL_D_CAPACITY_KEYS — derate both paths. Vladimir 2026-06-23.
          const paHeatContrib = (Number.isFinite(backendPaHeat)
            ? backendPaHeat
            : paCfm > 0 ? 1.08 * paCfm * (T_pa_winter - T_room_w) : 0) * paDerateRender;
          const totalHeat = paHeatContrib + heat_total;
          // Heating LWT comes directly from the coil tables via backend
          // (modulated heating_optimal mirrored onto speeds[0] for PA).
          const heat_gpm = parseFloat(heatRow.total_water_flow_gpm)
            || parseFloat(heatRow.water_flow_gpm)
            || parseFloat(heatOpt.water_flow_gpm)
            || 0;
          const lwtHeat = parseFloat(heatRow.leaving_water_f)
            || parseFloat(heatOpt.leaving_water_f)
            || T_water_in_heat;
          const dtHeat = T_water_in_heat - lwtHeat;
          const dtCool = lwtCool - T_water_in_cool;

          return (
            <>
              <Section title="Cooling — Primary-Air (Induction) Selection">
                <div className="mb-2 grid grid-cols-2 md:grid-cols-6 gap-2 text-xs">
                  <div>
                    <div className="text-muted-foreground">PA Flow</div>
                    <div className="font-semibold">{fmtKind(paCfm, "flow", isSI)} {unitLabel("flow", isSI)}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Nozzle</div>
                    <div className="font-semibold text-emerald-700 dark:text-emerald-400">{nozzle}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Static Pressure</div>
                    <div className="font-semibold">{staticP != null ? `${fmtKind(staticP, "staticPressure", isSI)} ${unitLabel("staticPressure", isSI)}` : "—"}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Induction Ratio</div>
                    <div className="font-semibold">{inductionRatio != null ? `${parseFloat(inductionRatio).toFixed(2)} : 1` : "—"}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Coil Mode</div>
                    <div className="font-semibold">
                      {coilLatentSuppressed
                        ? <span className="text-amber-700 dark:text-amber-400">Sensible only<br/><span className="text-[10px] text-amber-600 dark:text-amber-500">(water {fmtKind(T_water_in_cool, "temp", isSI)}{unitLabel("temp", isSI)} &gt; room DP {fmtKind(roomDewpoint, "temp", isSI)}{unitLabel("temp", isSI)} − 2)</span></span>
                        : <span className="text-blue-700 dark:text-blue-400">Sensible + Latent</span>}
                    </div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">NC Level</div>
                    {(() => {
                      // ACB NC at the PA operating point. Backend stamps it
                      // onto optimal_point and mirrors onto the pa speed row
                      // (acb_noise.get_acb_nc). Prefer optimal_point — that's
                      // post-modulation — then fall back to the speed row.
                      const ncVal = best?.optimal_point?.nc_level ?? s?.nc_level;
                      return (
                        <div className="font-semibold" data-testid="room-airside-nc">
                          {ncVal != null ? Math.round(ncVal) : "—"}
                        </div>
                      );
                    })()}
                  </div>
                </div>
                <Table className="text-xs [&_th]:h-7 [&_th]:pt-0.5 [&_th]:pb-1.5 [&_td]:pt-0.5 [&_td]:pb-1.5 [&_td]:align-middle border-separate border-spacing-0">
                  <TableHeader>
                    <TableRow>
                      <TableHead></TableHead>
                      <TableHead className="text-right">PA Sens<br/>({unitLabel("capacity", isSI)})</TableHead>
                      <TableHead className="text-right">PA Lat<br/>({unitLabel("capacity", isSI)})</TableHead>
                      <TableHead className="text-right">Coil Sens<br/>({unitLabel("capacity", isSI)})</TableHead>
                      {!(isACBRoom || coilLatentSuppressed) && <TableHead className="text-right">Coil Lat<br/>({unitLabel("capacity", isSI)})</TableHead>}
                      <TableHead className="text-right font-semibold">Total Sens<br/>({unitLabel("capacity", isSI)})</TableHead>
                      {!isACBRoom && <TableHead className="text-right font-semibold">Total Lat<br/>({unitLabel("capacity", isSI)})</TableHead>}
                      {/* Grand-total capacity column — shown for non-ACB PA models only.
                          ACB convention: 4 columns (PA Sens, PA Lat, Coil Sens, Total Sens);
                          a separate "Total Capacity" column is redundant and omitted. */}
                      {!isACBRoom && <TableHead className="text-right font-bold bg-emerald-100 dark:bg-emerald-950/40">Total Capacity<br/>({unitLabel("capacity", isSI)})</TableHead>}
                      <TableHead className="text-right">{unitLabel("waterFlow", isSI)}</TableHead>
                      <TableHead className="text-right">LWT<br/>({unitLabel("temp", isSI)})</TableHead>
                      <TableHead className="text-right">ΔT<br/>({unitLabel("temp", isSI)})</TableHead>
                      <TableHead className="text-right">PD<br/>({unitLabel("pressure", isSI)})</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow className="bg-emerald-50 dark:bg-emerald-950/30 border-t-[6px] border-white h-8 [&>td]:py-0 [&>td]:align-middle">
                      <TableCell className="font-semibold">Cooling</TableCell>
                      <TableCell className="text-right">{fmtKind(paSensCool, "capacity", isSI)}</TableCell>
                      <TableCell className="text-right">{fmtKind(paLatCool, "capacity", isSI)}</TableCell>
                      <TableCell className="text-right">{fmtKind(coilSensCool, "capacity", isSI)}</TableCell>
                      {!(isACBRoom || coilLatentSuppressed) && <TableCell className="text-right">{fmtKind(coilLatCool, "capacity", isSI)}</TableCell>}
                      <TableCell className="text-right font-semibold">{fmtKind(totalSensCool, "capacity", isSI)}</TableCell>
                      {!isACBRoom && <TableCell className="text-right font-semibold">{fmtKind(totalLatCool, "capacity", isSI)}</TableCell>}
                      {!isACBRoom && <TableCell className="text-right font-bold bg-emerald-100 dark:bg-emerald-950/40">{fmtKind(totalSensCool + totalLatCool, "capacity", isSI)}</TableCell>}
                      <TableCell className="text-right">{cooling_gpm > 0 ? fmtKind(cooling_gpm, "waterFlow", isSI) : "—"}</TableCell>
                      <TableCell className="text-right">{fmtKind(lwtCool, "temp", isSI)}</TableCell>
                      <TableCell className="text-right">{dtCool > 0 ? dtCool.toFixed(1) : "—"}</TableCell>
                      <TableCell className="text-right">{fmtKind(s.water_pd_fth2o, "pressure", isSI)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Section>

              {showHeating && (
                <Section title="Heating — Primary-Air (Induction) Selection">
                <Table className="text-xs [&_th]:h-7 [&_th]:pt-0.5 [&_th]:pb-1.5 [&_td]:pt-0.5 [&_td]:pb-1.5 [&_td]:align-middle border-separate border-spacing-0">
                  <TableHeader>
                    <TableRow>
                      <TableHead></TableHead>
                      <TableHead className="text-right">PA Contribution<br/>({unitLabel("capacity", isSI)})</TableHead>
                      <TableHead className="text-right">Coil Heat<br/>({unitLabel("capacity", isSI)})</TableHead>
                      <TableHead className="text-right font-semibold">Total<br/>({unitLabel("capacity", isSI)})</TableHead>
                      <TableHead className="text-right">{unitLabel("waterFlow", isSI)}</TableHead>
                      <TableHead className="text-right">LWT<br/>({unitLabel("temp", isSI)})</TableHead>
                      <TableHead className="text-right">ΔT<br/>({unitLabel("temp", isSI)})</TableHead>
                      <TableHead className="text-right">PD<br/>({unitLabel("pressure", isSI)})</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow className="bg-orange-50 dark:bg-orange-950/30 border-t-[6px] border-white h-8 [&>td]:py-0 [&>td]:align-middle">
                      <TableCell className="font-semibold">Heating</TableCell>
                      <TableCell className={`text-right ${paHeatContrib < 0 ? "text-rose-600" : ""}`}>{fmtKind(paHeatContrib, "capacity", isSI)}</TableCell>
                      <TableCell className="text-right">{fmtKind(heat_total, "capacity", isSI)}</TableCell>
                      <TableCell className="text-right font-semibold">{fmtKind(totalHeat, "capacity", isSI)}</TableCell>
                      <TableCell className="text-right">{heat_gpm > 0 ? fmtKind(heat_gpm, "waterFlow", isSI) : "—"}</TableCell>
                      <TableCell className="text-right">{fmtKind(lwtHeat, "temp", isSI)}</TableCell>
                      <TableCell className="text-right">{dtHeat > 0 ? dtHeat.toFixed(1) : "—"}</TableCell>
                      <TableCell className="text-right">{fmtKind(heatRow.water_pd_fth2o, "pressure", isSI)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
                </Section>
              )}
            </>
          );
        }

        return (
          <Section title="Cooling — Air Side (per fan speed)">
            <Table className="text-xs [&_th]:h-7 [&_th]:pt-0.5 [&_th]:pb-1.5 [&_td]:pt-0.5 [&_td]:pb-1.5 [&_td]:align-middle">
              <TableHeader>
                <TableRow>
                  <TableHead>Speed</TableHead>
                  <TableHead className="text-right">CFM ({unitLabel("flow", isSI)})</TableHead>
                  <TableHead className="text-right">Sensible ({unitLabel("capacity", isSI)})</TableHead>
                  <TableHead className="text-right">Latent ({unitLabel("capacity", isSI)})</TableHead>
                  <TableHead className="text-right">Total ({unitLabel("capacity", isSI)})</TableHead>
                  <TableHead className="text-right">LWT ({unitLabel("temp", isSI)})</TableHead>
                  <TableHead className="text-right">ΔT ({unitLabel("temp", isSI)})</TableHead>
                  <TableHead className="text-right">Power (W)</TableHead>
                  <TableHead className="text-right">NC</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {speeds.map((s) => {
                  const a = s.air_side || {};
                  return (
                    <TableRow key={s.speed}>
                      <TableCell className="capitalize">{s.speed}</TableCell>
                      <TableCell className="text-right">{fmtKind((s.total_supply_cfm ?? (((s.primary_cfm || 0) + (s.secondary_cfm || 0)) || s.cfm)) ?? null, "flow", isSI)}</TableCell>
                      <TableCell className="text-right">{fmtKind(a.air_sensible_btuh ?? s.final_sensible_btuh, "capacity", isSI)}</TableCell>
                      <TableCell className="text-right">{fmtKind(a.air_latent_btuh ?? s.final_latent_btuh, "capacity", isSI)}</TableCell>
                      <TableCell className="text-right">{fmtKind(a.air_total_btuh ?? s.final_total_btuh, "capacity", isSI)}</TableCell>
                      <TableCell className="text-right">{fmtKind(s.leaving_water_f, "temp", isSI)}</TableCell>
                      <TableCell className="text-right">{fmtKind(s.water_delta_t_f, "temp", isSI)}</TableCell>
                      <TableCell className="text-right">
                        {s.fan_watts != null ? Math.round(s.fan_watts) : "—"}
                      </TableCell>
                      <TableCell className="text-right">{s.nc_level != null ? Math.round(s.nc_level) : "—"}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <p className="mt-2 text-[10px] text-muted-foreground italic">
              Independently Measured NC Values - Pending Certification
            </p>
          </Section>
        );
      })()}

      {showChart && raw?.results?.[0]?.cooling?.psych_chart?.points && (
        <Section title="Psychrometric Chart">
          <PsychrometricChart data={raw.results[0].cooling.psych_chart} isSI={isSI} />
        </Section>
      )}
    </>
  );
}

export default function RoomReportPage({ project, room, result, isSI, onBack, onPdfGenerated, showChart, captureMode = false, lockedLength = null, onUpdateLength }) {
  const reportRef = React.useRef(null);
  // When lockedLength is provided (sweep-row click in Single Unit), seed the
  // override so the correct length is shown immediately and hide the dropdown.
  const [lengthOverride, setLengthOverride] = React.useState(lockedLength);
  const [circuitsOverride, setCircuitsOverride] = React.useState(null);

  const isN = isModelN(room?.unit_model);

  // Available lengths from the raw API response (multi-length endpoint only).
  // Use per-length modulated optimal_point values so the picker is consistent
  // with the banner and Summary tab (B.7 Fix 4).
  // Key on (length_ft, circuits) so two distinct circuit rows at the same length
  // appear as separate options.
  const availableLengths = React.useMemo(() => {
    if (isN || !result?.raw?.results) return [];
    const arr = result.raw.results;
    const seen = new Set();
    return [...arr]
      .map((r) => {
        const { opt } = pickCascadeOperatingPoint(r, project);
        // AHRI 1335 single-basis lock: always use air-side delivered (Q_r).
        const totalForLength = opt.air_total_btuh ?? null;
        return {
          length_ft: r.length_ft,
          circuits: r.circuits ?? null,
          total: totalForLength ?? 0,
          sensible: opt.final_sensible_btuh ?? 0,
          nozzle_warning: r.nozzle_warning ?? null,
        };
      })
      .filter((x) => {
        if (x.length_ft == null) return false;
        const k = `${x.length_ft}__${x.circuits ?? ""}`;
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      })
      .sort((a, b) => a.length_ft - b.length_ft || (a.circuits ?? 0) - (b.circuits ?? 0));
  }, [result, isN, project]);

  // Apply length (and optionally circuits) override
  const effectiveResult = React.useMemo(() => {
    if (!lengthOverride || isN || !result) return result;
    const orig = result?.raw?.results || [];
    const picked = circuitsOverride != null
      ? (orig.find((r) => r.length_ft === lengthOverride && r.circuits === circuitsOverride) || orig.find((r) => r.length_ft === lengthOverride))
      : orig.find((r) => r.length_ft === lengthOverride);
    if (!picked) return result;
    return {
      ...result,
      raw: { ...result.raw, results: [picked, ...orig.filter((r) => r !== picked)] },
    };
  }, [result, lengthOverride, circuitsOverride, isN]);

  // Early return AFTER all hook calls
  if (!room || !result) return null;

  const dc = project.designConditions || {};
  const advanced = project.advanced || {};
  // Outdoor + Country block only relevant for Model N / N-f (which use
  // outdoor design air directly via the PAC). For induction / room-air
  // models we suppress those rows so the report no longer leaks a stored
  // default country / ASHRAE city.
  const needsOutdoor = room?.unit_model === "Model N" || room?.unit_model === "Model N-f";
  const displayName = modelDisplayName(room, effectiveResult?.raw, isSI, effectiveResult?.summary);
  const handlePrint = () => window.print();
  const handlePDF = async () => {
    await generateRoomReportPDF({
      project,
      room,
      result: effectiveResult,
      displayName,
      sourceEl: reportRef.current,
      isSI,
    });
    if (onPdfGenerated) onPdfGenerated();
  };

  // Delivered capacity in the room (room-relative air-side numbers)
  const delivered = computeDeliveredCapacity(effectiveResult, isN, project, room);

  // Chart visibility: when `showChart` is explicitly provided (e.g. submittal
  // capture for EWS/ACB), honor that. Otherwise default to true so the live
  // on-screen view keeps showing the chart whenever the data is present.
  const chartEnabled = typeof showChart === "boolean" ? showChart : true;

  // Heating visibility: hide all heating UI when the room has no heating
  // demand AND the project has no heating-water temperature configured.
  const heatWaterTempRP = parseFloat(
    project?.designConditions?.water_entering_heat_f
    ?? project?.designConditions?.water_entering_winter_f
  );
  const heatingWaterConfiguredRP = Number.isFinite(heatWaterTempRP) && heatWaterTempRP > 0;
  const roomHeating = parseFloat(room.heating_btuh) || 0;
  const showHeatingRP = roomHeating > 0 || heatingWaterConfiguredRP;

  return (
    <div className="space-y-2" data-testid="room-report-page">
      {/* Toolbar — hidden in capture mode so it never appears in the PDF */}
      {!captureMode && (
      <div className="flex items-center justify-between print:hidden">
        <Button variant="outline" size="sm" onClick={onBack} data-testid="room-report-back">
          <ChevronLeft className="h-4 w-4 mr-1" /> Back to Summary
        </Button>
        <div className="flex items-center gap-2">
          {availableLengths.length > 1 && !lockedLength && (() => {
            const hasMultiCkt = availableLengths.some((l) => l.circuits != null);
            const firstResult = effectiveResult?.raw?.results?.[0];
            const currentItemVal = (() => {
              const l = lengthOverride ?? firstResult?.length_ft ?? null;
              const c = circuitsOverride ?? firstResult?.circuits ?? null;
              if (l == null) return "";
              return hasMultiCkt ? `${l}__${c ?? ""}` : String(l);
            })();
            return (
              <div className="flex items-center gap-1 text-xs">
                <span className="text-muted-foreground">Pick length:</span>
                <TooltipProvider>
                  <Select
                    value={currentItemVal}
                    onValueChange={(v) => {
                      const [lenPart, cktPart] = v.split("__");
                      const newLen = parseInt(lenPart, 10);
                      const newCkt = cktPart !== "" && cktPart !== undefined ? parseInt(cktPart, 10) : null;
                      setLengthOverride(newLen);
                      setCircuitsOverride(newCkt);
                      if (onUpdateLength) onUpdateLength(newLen, newCkt ?? undefined);
                    }}
                    data-testid="room-report-length-override"
                  >
                    <SelectTrigger className="h-7 text-xs px-2 w-[180px]">
                      <SelectValue placeholder="Pick length…" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableLengths.map((l) => {
                        const itemVal = hasMultiCkt
                          ? `${l.length_ft}__${l.circuits ?? ""}`
                          : String(l.length_ft);
                        const lenLabel = isSI ? `${Math.round(parseFloat(l.length_ft) * 300)} mm` : `${l.length_ft} ft`;
                        const cktLabel = l.circuits != null ? ` (${l.circuits} ckt)` : "";
                        return (
                          <SelectItem key={itemVal} value={itemVal}>
                            <span className="flex items-center gap-1.5">
                              {lenLabel}{cktLabel} — {fmtKind(l.total, "capacity", isSI)} {unitLabel("capacity", isSI)}
                              {l.nozzle_warning && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="inline-flex">
                                      <AlertTriangle className="h-3.5 w-3.5 text-amber-500 shrink-0" />
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent>{l.nozzle_warning.message}</TooltipContent>
                                </Tooltip>
                              )}
                            </span>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </TooltipProvider>
              </div>
            );
          })()}
          <Button variant="outline" size="sm" onClick={handlePrint} data-testid="room-report-print">
            <Printer className="h-4 w-4 mr-1" /> Print
          </Button>
          <Button size="sm" onClick={handlePDF} data-testid="room-report-pdf">
            <FileDown className="h-4 w-4 mr-1" /> PDF Report
          </Button>
        </div>
      </div>
      )}

      {/* Branded header */}
      <div ref={reportRef} className="border border-border rounded p-3 bg-card print:border-0 print:p-0 print:bg-white">
        <div data-pdf-inner-brand className="flex items-center justify-between mb-2 border-b pb-2 gap-4">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <img
              src="/zamba-logo.png"
              alt="ZAMBA"
              className="h-9 flex-shrink-0"
              onError={(e) => (e.currentTarget.style.display = "none")}
            />
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">
                Project
              </div>
              <div className="font-semibold text-sm truncate" title={project.name}>
                {project.name || "—"}
                {project.number && (
                  <span className="text-muted-foreground font-normal ml-2">
                    ({project.number})
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="text-right flex-shrink-0">
            <div className="text-base font-bold">{room.name || "Room"}</div>
            <div className="text-xs text-muted-foreground">{displayName}</div>
          </div>
        </div>

        {/* Selected model — prominent banner above the green Delivered box */}
        {result.ok && (() => {
          const selCircuits = effectiveResult?.summary?.selected_circuits ?? null;
          const circuitSel = effectiveResult?.summary?.circuit_selection ?? null;
          return (
            <div
              className="mb-2 flex items-baseline gap-3 flex-wrap border-l-4 border-emerald-600 pl-3 py-0.5"
              data-testid="room-report-selected-model"
            >
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">
                Selected Unit
              </span>
              <span className="text-2xl md:text-3xl font-bold text-foreground">
                {displayName}
              </span>
              {selCircuits != null && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="text-sm font-medium px-2 py-0.5 rounded border border-border bg-muted text-muted-foreground cursor-default">
                        {selCircuits} ckt
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      {circuitSel?.reason
                        ? circuitSel.reason
                        : `${selCircuits}-circuit configuration`}
                      {circuitSel?.alternative_circuits != null && (
                        <span className="block mt-0.5 text-muted-foreground">
                          Alternative: {circuitSel.alternative_circuits}-circuit available via Refine
                        </span>
                      )}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          );
        })()}

        {/* Delivered capacity highlight (room-relative) */}
        {delivered && result.ok && (() => {
          // NC level for the selected operating point. Backend stamps it on
          // summary.nc_level for ACB (acb_noise.get_acb_nc), FACB
          // (shw_calculator.get_nc_at_cfm), and Model N (model_n_calculator).
          // Show only when present — non-ACB rooms without NC data display
          // an em-dash rather than a missing tile, keeping the row aligned.
          const ncVal = result?.summary?.nc_level;
          const hasHeat = !!showHeatingRP;
          // Tile count: Total, Sens, Latent, [Heating], Airflow, NC.
          // grid-cols-6 fits all on one row in the engineering PDF page width.
          const colsClass = hasHeat ? "md:grid-cols-6" : "md:grid-cols-5";
          const colspanClass = hasHeat ? "md:col-span-6" : "md:col-span-5";
          return (
            <div className={`grid grid-cols-2 ${colsClass} gap-1.5 mb-2 p-2 rounded border-2 border-emerald-200 dark:border-emerald-900 bg-emerald-50 dark:bg-emerald-950/30`}>
              <div className={`${colspanClass} text-xs font-semibold text-emerald-800 dark:text-emerald-300 uppercase tracking-wide`}>
                {bannerLabelForBasis(delivered.basis)}
              </div>
              <div>
                <div className="text-[10px] text-muted-foreground uppercase">Total Cooling</div>
                <div className="font-bold text-base">{fmtKind(delivered.total, "capacity", isSI)} <span className="text-xs font-normal">{unitLabel("capacity", isSI)}</span></div>
              </div>
              <div>
                <div className="text-[10px] text-muted-foreground uppercase">Sensible</div>
                <div className="font-bold text-base">{fmtKind(delivered.sensible, "capacity", isSI)} <span className="text-xs font-normal">{unitLabel("capacity", isSI)}</span></div>
              </div>
              <div>
                <div className="text-[10px] text-muted-foreground uppercase">Latent</div>
                <div className="font-bold text-base">{fmtKind(delivered.latent, "capacity", isSI)} <span className="text-xs font-normal">{unitLabel("capacity", isSI)}</span></div>
              </div>
              {hasHeat && (
                <div>
                  <div className="text-[10px] text-muted-foreground uppercase">Heating</div>
                  <div className="font-bold text-base">{fmtKind(delivered.heating, "capacity", isSI)} <span className="text-xs font-normal">{unitLabel("capacity", isSI)}</span></div>
                </div>
              )}
              <div>
                <div className="text-[10px] text-muted-foreground uppercase">Total Airflow</div>
                <div className="font-bold text-base">{fmtKind(delivered.airflow, "flow", isSI)} <span className="text-xs font-normal">{unitLabel("flow", isSI)}</span></div>
              </div>
              <div>
                <div className="text-[10px] text-muted-foreground uppercase">NC Level</div>
                <div className="font-bold text-base" data-testid="room-banner-nc">{ncVal != null ? Math.round(ncVal) : "—"}</div>
              </div>
            </div>
          );
        })()}

        {/* Project Info */}
        <Section title="Project Information">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <div>
              <div className="text-muted-foreground">Project</div>
              <div className="font-semibold">{project.name || "—"}</div>
            </div>
            <div>
              <div className="text-muted-foreground">Number</div>
              <div className="font-semibold">{project.number || "—"}</div>
            </div>
            <div>
              <div className="text-muted-foreground">Address</div>
              <div className="font-semibold">{project.address || "—"}</div>
            </div>
            <div>
              <div className="text-muted-foreground">City / State</div>
              <div className="font-semibold">
                {[project.cityText, project.state].filter(Boolean).join(", ") || "—"}
              </div>
            </div>
            {needsOutdoor && (
              <div>
                <div className="text-muted-foreground">Country</div>
                <div className="font-semibold">{(dc.country === "__other__" ? dc.country_other : dc.country) || "—"}</div>
              </div>
            )}
            {needsOutdoor && dc.country !== "__other__" && dc.city && dc.city !== "__manual__" && (
              <div className="md:col-span-3">
                <div className="text-muted-foreground">Design Reference City (ASHRAE)</div>
                <div className="font-semibold">
                  {dc.city}{dc.country ? `, ${dc.country}` : ""}
                </div>
              </div>
            )}
          </div>
        </Section>

        {/* Design Conditions */}
        <Section title="Design Conditions (Project)">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {needsOutdoor && (
              <div>
                <div className="text-muted-foreground">Outdoor DB / WB</div>
                <div className="font-semibold">
                  {fmtKind(dc.outdoor_db_f, "temp", isSI)} / {fmtKind(dc.outdoor_wb_f, "temp", isSI)} {unitLabel("temp", isSI)}
                </div>
              </div>
            )}
            {needsOutdoor && (
              <div>
                <div className="text-muted-foreground">Outdoor Winter DB</div>
                <div className="font-semibold">
                  {fmtKind(dc.outdoor_db_winter_f, "temp", isSI)} {unitLabel("temp", isSI)}
                </div>
              </div>
            )}
            <div>
              <div className="text-muted-foreground">Room DB / RH (Summer)</div>
              <div className="font-semibold">
                {fmtKind(dc.room_db_f, "temp", isSI)} {unitLabel("temp", isSI)} / {dc.room_rh}%
              </div>
            </div>
            {(dc.room_db_winter_f || dc.room_rh_winter) && (
              <div>
                <div className="text-muted-foreground">Room DB / RH (Winter)</div>
                <div className="font-semibold">
                  {dc.room_db_winter_f ? fmtKind(dc.room_db_winter_f, "temp", isSI) : "—"} {unitLabel("temp", isSI)} / {dc.room_rh_winter || "—"}%
                </div>
              </div>
            )}
            <div>
              <div className="text-muted-foreground">Water EWT (cool / heat)</div>
              <div className="font-semibold">
                {fmtKind(dc.water_entering_f, "temp", isSI)} / {dc.water_entering_winter_f ? fmtKind(dc.water_entering_winter_f, "temp", isSI) : "—"} {unitLabel("temp", isSI)}
              </div>
            </div>
            {needsOutdoor && (
              <div>
                <div className="text-muted-foreground">Altitude</div>
                <div className="font-semibold">{fmtKind(dc.altitude_ft, "altitude", isSI)} {isSI ? "m" : "ft"}</div>
              </div>
            )}
            <div>
              <div className="text-muted-foreground">Glycol</div>
              <div className="font-semibold">
                {advanced.use_glycol ? `${advanced.glycol_type} ${advanced.glycol_percentage}%` : "None"}
              </div>
            </div>
            <div>
              <div className="text-muted-foreground">Air-On (Cool / Heat)</div>
              <div className="font-semibold">
                {advanced.air_on_temp_f
                  ? <>{fmtKind(advanced.air_on_temp_f, "temp", isSI)} {unitLabel("temp", isSI)}</>
                  : <span className="text-muted-foreground">auto</span>}
                {" / "}
                {advanced.air_on_temp_heating_f
                  ? <>{fmtKind(advanced.air_on_temp_heating_f, "temp", isSI)} {unitLabel("temp", isSI)}</>
                  : <span className="text-muted-foreground">auto</span>}
              </div>
            </div>
            {/* PA EAT / RH — for PA induction models only */}
            {(dc.pa_eat_db_f != null || dc.pa_eat_rh != null) && (
              <div>
                <div className="text-muted-foreground">PA EAT DB / RH</div>
                <div className="font-semibold">
                  {dc.pa_eat_db_f != null && dc.pa_eat_db_f !== "" ? `${fmtKind(dc.pa_eat_db_f, "temp", isSI)} ${unitLabel("temp", isSI)}` : "—"}
                  {" / "}
                  {dc.pa_eat_rh != null && dc.pa_eat_rh !== "" ? `${dc.pa_eat_rh}%` : "—"}
                </div>
              </div>
            )}
            {/* PA External Static Pressure */}
            {advanced.pa_pressure_in_wg && parseFloat(advanced.pa_pressure_in_wg) > 0 && (
              <div>
                <div className="text-muted-foreground">PA External Static</div>
                <div className="font-semibold">
                  {fmtKind(advanced.pa_pressure_in_wg, "staticPressure", isSI)} {unitLabel("staticPressure", isSI)}
                </div>
              </div>
            )}
            {/* Water flow override (project-level) */}
            {advanced.water_flow_gpm && parseFloat(advanced.water_flow_gpm) > 0 && (
              <div>
                <div className="text-muted-foreground">Water Flow Override</div>
                <div className="font-semibold">
                  {fmtKind(advanced.water_flow_gpm, "waterFlow", isSI)} {unitLabel("waterFlow", isSI)}
                </div>
              </div>
            )}
            {/* Max water pressure drop */}
            {advanced.max_water_dp_fth2o && parseFloat(advanced.max_water_dp_fth2o) > 0 && (
              <div>
                <div className="text-muted-foreground">Max Water ΔP</div>
                <div className="font-semibold">
                  {fmtKind(advanced.max_water_dp_fth2o, "pressure", isSI)} {unitLabel("pressure", isSI)}
                </div>
              </div>
            )}
            {/* Max unit length per model — show this room's model only */}
            {(() => {
              const cap = (advanced.maxLengthByModel || {})[room?.unit_model];
              if (cap == null || cap === "") return null;
              const capLbl = isSI
                ? `${Math.round(parseFloat(cap) * 300)} mm`
                : `${cap} ft`;
              return (
                <div>
                  <div className="text-muted-foreground">Max Length ({room.unit_model})</div>
                  <div className="font-semibold">{capLbl}</div>
                </div>
              );
            })()}
            {/* Model O-2 piping (serial / parallel) */}
            {room?.unit_model === "Model Op" && advanced.o2_circuit && (
              <div>
                <div className="text-muted-foreground">Coil Piping (O-2)</div>
                <div className="font-semibold capitalize">{advanced.o2_circuit}</div>
              </div>
            )}
            {/* Non-condensing mode (Model N variants) */}
            {advanced.non_condensing_mode && (
              <div>
                <div className="text-muted-foreground">Coil Mode</div>
                <div className="font-semibold">Non-condensing (dry coil)</div>
              </div>
            )}
            {/* Heat-recovery type (Model N) */}
            {needsOutdoor && advanced.recovery_type && (
              <div>
                <div className="text-muted-foreground">Heat Recovery</div>
                <div className="font-semibold">{advanced.recovery_type}</div>
              </div>
            )}
          </div>
        </Section>

        {/* Room-specific inputs */}
        <Section title="Room Inputs">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {/* Row 1: identity + flows + static pressure */}
            <div>
              <div className="text-muted-foreground">Unit Model</div>
              <div className="font-semibold">{displayName}</div>
            </div>
            {(() => {
              const pipes = project?.advanced?.no_coil_pipes ?? 2;
              return (
                <div>
                  <div className="text-muted-foreground">Coil Pipes</div>
                  <div className="font-semibold">{pipes}-pipe</div>
                </div>
              );
            })()}
            <div>
              <div className="text-muted-foreground">OA Flow</div>
              <div className="font-semibold">{fmtKind(room.oa_flow_cfm, "flow", isSI)} {unitLabel("flow", isSI)}</div>
            </div>
            <div>
              <div className="text-muted-foreground">EA Flow</div>
              <div className="font-semibold">{fmtKind(room.ea_flow_cfm, "flow", isSI)} {unitLabel("flow", isSI)}</div>
            </div>
            {isACB(room.unit_model) ? (() => {
              const hasUserInput = (() => { const v = parseFloat(room?.pa_static_pressure_inwg); return Number.isFinite(v) && v > 0; })();
              const resolvedSp = getRoomStaticPressureInwg(room, effectiveResult, project);
              return (
                <div>
                  <div className="text-muted-foreground">Static Pressure</div>
                  <div className="font-semibold">
                    {resolvedSp != null
                      ? `${fmtKind(resolvedSp, "staticPressure", isSI)} ${unitLabel("staticPressure", isSI)}`
                      : "—"}
                  </div>
                  {!hasUserInput && resolvedSp != null && (
                    <div className="text-xs text-muted-foreground">(default)</div>
                  )}
                </div>
              );
            })() : <div />}

            {/* Row 2: capacity targets + heating */}
            <div>
              <div className="text-muted-foreground">Target Total Cooling</div>
              <div className="font-semibold">{fmtKind(room.cooling_total_btuh, "capacity", isSI)} {unitLabel("capacity", isSI)}</div>
            </div>
            <div>
              <div className="text-muted-foreground">Target Sensible</div>
              <div className="font-semibold">{fmtKind(room.cooling_sensible_btuh, "capacity", isSI)} {unitLabel("capacity", isSI)}</div>
            </div>
            <div>
              <div className="text-muted-foreground">Target Latent (derived)</div>
              <div className="font-semibold">{fmtKind(room.cooling_latent_btuh, "capacity", isSI)} {unitLabel("capacity", isSI)}</div>
            </div>
            <div>
              <div className="text-muted-foreground">Heating Demand</div>
              <div className="font-semibold">{fmtKind(room.heating_btuh, "capacity", isSI)} {unitLabel("capacity", isSI)}</div>
            </div>
          </div>
        </Section>

        {/* Results — render one detail block per sub-unit when heterogeneous */}
        {(() => {
          const plan = effectiveResult?.summary?.plan;
          const subUnits =
            plan?.units && plan.units.length > 1
              ? plan.units
                  .filter((u) => u.result?.ok)
                  .map((u) => ({
                    label: `${u.model}${u.length_ft ? ` — ${unitLengthLabel(u.length_ft, isSI)}${isSI ? " mm" : " ft"}` : ""}${u.qty > 1 ? ` × ${u.qty}` : ""}`,
                    raw: u.result.raw,
                    summary: u.result.summary,
                    isModelN: isModelN(u.model),
                    model: u.model,
                  }))
              : null;

          if (subUnits && subUnits.length > 0) {
            return (
              <>
                {subUnits.map((su, i) => (
                  <div key={i}>
                    <div className="mt-3 mb-1 px-3 py-1.5 rounded bg-foreground text-background text-sm font-semibold">
                      Unit {i + 1}: {su.label}
                    </div>
                    {su.isModelN ? (
                      <ModelNDetail raw={su.raw} isSI={isSI} showChart={chartEnabled} unitModel={su.model} />
                    ) : isModelO(su.model) ? (
                      <ModelODetail raw={su.raw} isSI={isSI} showChart={chartEnabled} unitModel={su.model} project={project} room={room} />
                    ) : (
                      <MultiLengthDetail raw={su.raw} summary={su.summary} isSI={isSI} project={project} room={room} showChart={chartEnabled} showHeating={showHeatingRP} />
                    )}
                  </div>
                ))}
                {!subUnits.some((su) => isModelO(su.model)) && (
                  <p className="text-[10px] text-muted-foreground mt-2 leading-relaxed italic" data-testid="room-report-sizing-footnote">
                    {formatSizingFootnote(project, [room])}
                  </p>
                )}
              </>
            );
          }

          if (effectiveResult.ok) {
            return isN ? (
              <>
                <ModelNDetail raw={effectiveResult.raw} isSI={isSI} showChart={chartEnabled} unitModel={room?.unit_model} />
                <p className="text-[10px] text-muted-foreground mt-2 leading-relaxed italic" data-testid="room-report-sizing-footnote">
                  {formatSizingFootnote(project, [room])}
                </p>
              </>
            ) : isModelO(room?.unit_model) ? (
              <ModelODetail raw={effectiveResult.raw} isSI={isSI} showChart={chartEnabled} unitModel={room?.unit_model} project={project} room={room} />
            ) : (
              <>
                <MultiLengthDetail raw={effectiveResult.raw} summary={effectiveResult.summary} isSI={isSI} project={project} room={room} showChart={chartEnabled} showHeating={showHeatingRP} />
                <p className="text-[10px] text-muted-foreground mt-2 leading-relaxed italic" data-testid="room-report-sizing-footnote">
                  {formatSizingFootnote(project, [room])}
                </p>
              </>
            );
          }
          return (
            <div className="text-sm text-rose-600 dark:text-rose-400 p-4 bg-rose-50 dark:bg-rose-950/30 rounded">
              Selection failed: {effectiveResult.error}
            </div>
          );
        })()}

        {/* Engineer's Notes — only rendered when notes are non-empty so the
            section is cleanly absent from PDF and screen when not provided.
            captureLiveSourceAndAddPages picks this up automatically because
            the Section component already carries data-pdf-section.
            Extra top margin separates the section from the preceding
            sizing-footnote line so the banner doesn't visually crowd it. */}
        {(room?.notes ?? "").trim() && (
          <div className="mt-3">
            <Section title="Engineer's Notes">
              <p
                className="text-xs text-foreground whitespace-pre-wrap leading-relaxed"
                data-testid="room-report-engineers-notes"
              >
                {room.notes.trim()}
              </p>
            </Section>
          </div>
        )}

        {/* Footer — hidden during PDF capture; the PDF has its own
            per-page universal footer drawn by jsPDF. Keep on-screen for
            the regular Room Report view. */}
        <div
          data-pdf-hide-in-capture
          className="text-[10px] text-muted-foreground text-center mt-4 border-t border-border pt-2 print:fixed print:bottom-2 print:left-0 print:right-0"
        >
          Generated by ZAMBA Product Selector — {new Date().toLocaleDateString(isSI ? "en-GB" : "en-US", { year: "numeric", month: "long", day: "numeric" })} —
          www.zambahvac.com
        </div>
      </div>
    </div>
  );
}

export { modelDisplayName };
